/**
 * FailureStats Page - Admin dashboard for system failure statistics
 *
 * Displays aggregated failure mode data computed by nightly batch job.
 * Shows recommendations only (not tracking implemented mitigations).
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, SpinnerSize } from "@fluentui/react";
import {
  ArrowTrendingRegular,
  ArrowTrendingDownRegular,
  SubtractRegular,
  LightbulbRegular,
  ArrowSyncRegular,
  InfoRegular,
} from "@fluentui/react-icons";
import { AppHeader } from "@shared/components";
import { usePermissions } from "@shared/auth";
import { getFailureStats } from "../api";
import type {
  FailureStatsResponse,
  FailureModeData,
  PriorityRecommendation,
} from "../api";
import styles from "./FailureStats.module.css";

/**
 * Format date for display
 */
function formatDate(dateString: string | undefined): string {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

/**
 * Format percentage for display
 */
function formatPercentage(value: number): string {
  return `${value.toFixed(1)}%`;
}

/**
 * Trend indicator component
 */
const TrendIndicator: React.FC<{ trend: string; direction: string }> = ({
  trend,
  direction,
}) => {
  const Icon =
    direction === "up"
      ? ArrowTrendingRegular
      : direction === "down"
        ? ArrowTrendingDownRegular
        : SubtractRegular;

  const className =
    direction === "up"
      ? styles.trendUp
      : direction === "down"
        ? styles.trendDown
        : styles.trendStable;

  return (
    <span className={`${styles.trend} ${className}`}>
      <Icon />
      <span>{trend}</span>
    </span>
  );
};

/**
 * Lever badge component
 */
const LeverBadge: React.FC<{ lever: string }> = ({ lever }) => {
  // Normalize lever name for CSS class
  const leverClass = lever.toLowerCase().replace(/[^a-z]/g, "_");

  return (
    <span className={`${styles.leverBadge} ${styles[`lever_${leverClass}`]}`}>
      {lever}
    </span>
  );
};

/**
 * Summary stat card component
 */
const StatCard: React.FC<{
  label: string;
  value: string | number;
  subtext?: string;
  tooltip?: string;
}> = ({ label, value, subtext, tooltip }) => (
  <div className={styles.statCard}>
    <div className={tooltip ? styles.statLabelWithTooltip : styles.statLabel}>
      <span className={styles.statLabel}>{label}</span>
      {tooltip && (
        <span className={styles.tooltipIcon}>
          <InfoRegular />
          <span className={styles.tooltipContent}>{tooltip}</span>
        </span>
      )}
    </div>
    <div className={styles.statValue}>{value}</div>
    {subtext && <div className={styles.statSubtext}>{subtext}</div>}
  </div>
);

/**
 * Recommendation card component
 */
const RecommendationCard: React.FC<{
  recommendation: PriorityRecommendation;
}> = ({ recommendation }) => (
  <div className={styles.recommendationCard}>
    <div className={styles.recommendationHeader}>
      <div className={styles.recommendationRank}>#{recommendation.rank}</div>
      <LeverBadge lever={recommendation.lever} />
      <div className={styles.recommendationStats}>
        {recommendation.failure_count} failures (
        {formatPercentage(recommendation.percentage)})
      </div>
    </div>
    <ul className={styles.actionList}>
      {recommendation.actions.map((action, index) => (
        <li key={index}>{action}</li>
      ))}
    </ul>
  </div>
);

export const FailureStats: React.FC = () => {
  const navigate = useNavigate();
  const { getAccessToken } = usePermissions();

  const [stats, setStats] = useState<FailureStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const result = await getFailureStats(token);

      if (result.success) {
        setStats(result.data);
      } else {
        setError(result.error.message);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load statistics",
      );
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className={styles.container}>
      <AppHeader currentApp="admin" />
      <main className={styles.main}>
        <div className={styles.headerSection}>
          <h1 className={styles.title}>Feedback Metrics</h1>
          <button
            className={styles.refreshButton}
            onClick={fetchStats}
            disabled={isLoading}
          >
            <ArrowSyncRegular />
            <span>Refresh</span>
          </button>
        </div>

        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size={SpinnerSize.large} label="Loading statistics..." />
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorMessage}>{error}</p>
            <button className={styles.retryButton} onClick={fetchStats}>
              Retry
            </button>
          </div>
        ) : !stats ? (
          <div className={styles.emptyContainer}>
            <h2 className={styles.emptyTitle}>No Statistics Available</h2>
            <p className={styles.emptyText}>
              The nightly batch job has not yet generated failure statistics.
              Statistics will be available after the first batch job run.
            </p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className={styles.summaryGrid}>
              <StatCard
                label="Total Feedback"
                value={stats.summary.total_feedback}
                subtext={`${stats.summary.positive_feedback} positive, ${stats.summary.negative_feedback} negative`}
              />
              <StatCard
                label="Negative Feedback"
                value={stats.summary.negative_feedback}
              />
              <StatCard
                label="Classification Rate"
                value={formatPercentage(
                  stats.summary.classification_rate * 100,
                )}
                tooltip="Percentage of negative feedback classified into a specific failure mode. A higher rate means more issues can be addressed with targeted fixes."
              />
              <StatCard
                label="Last Updated"
                value={formatDate(stats.generated_at)}
              />
            </div>

            {/* Failure Mode Distribution Table */}
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Failure Mode Distribution</h2>
              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Failure Mode</th>
                      <th>Count</th>
                      <th>%</th>
                      <th>Trend (30d)</th>
                      <th>Architectural Lever</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.failure_modes
                      .filter((mode) => mode.count > 0)
                      .map((mode) => (
                        <tr key={mode.key}>
                          <td className={styles.modeName}>{mode.name}</td>
                          <td>{mode.count}</td>
                          <td>{formatPercentage(mode.percentage)}</td>
                          <td>
                            <TrendIndicator
                              trend={mode.trend}
                              direction={mode.trend_direction}
                            />
                          </td>
                          <td>
                            <LeverBadge lever={mode.lever} />
                          </td>
                        </tr>
                      ))}
                    {stats.failure_modes.filter((mode) => mode.count > 0)
                      .length === 0 && (
                      <tr>
                        <td colSpan={6} className={styles.noData}>
                          No failures recorded in this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Priority Recommendations */}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <LightbulbRegular className={styles.sectionIcon} />
                <h2 className={styles.sectionTitle}>
                  Priority Recommendations
                </h2>
              </div>
              <p className={styles.sectionDescription}>
                Based on failure frequency, focus improvements on these
                architectural areas:
              </p>
              {stats.priority_recommendations.length > 0 ? (
                <div className={styles.recommendationsGrid}>
                  {stats.priority_recommendations.map((rec) => (
                    <RecommendationCard key={rec.rank} recommendation={rec} />
                  ))}
                </div>
              ) : (
                <div className={styles.noRecommendations}>
                  <p>
                    No recommendations available yet. Collect more feedback to
                    generate insights.
                  </p>
                </div>
              )}
            </div>

            {/* Period Info */}
            <div className={styles.periodInfo}>
              Analysis period: {formatDate(stats.period.start)} -{" "}
              {formatDate(stats.period.end)}
            </div>
          </>
        )}
      </main>
    </div>
  );
};
